import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { Loading } from "source/shared/components"
import { Heading } from "@planningcenter/doxy-web"

import { APIError } from "source/shared/types"

type ResponseStatus = "loading" | "success" | "error"

interface GroupVertex {
  type: "Group"
  attributes: {
    name: string
  }
}

interface PersonVertex {
  type: "Person"
  attributes: {
    email_address: string
  }
}

interface RevertTokenVertex {
  type: "UnsubscribeToken" | "SubscribeToken"
  attributes: {
    value: string
  }
}

type ResponseData = [GroupVertex, PersonVertex, RevertTokenVertex]

export default function DigestSubscriptionsEdit() {
  const { token } = useParams()
  const [status, setStatus] = useState<ResponseStatus>("loading")
  const [data, setData] = useState<ResponseData | undefined>()
  const [error, setError] = useState<APIError | undefined>()

  useEffect(() => {
    sessionApiClient
      .post("/groups/v2/group_digest_subscription", {
        data: { attributes: { token } },
      })
      .then((response: { data: ResponseData }) => {
        setData(response.data)
        setStatus("success")
      })
      .catch((error: APIError) => {
        setError(error)
        setStatus("error")
      })
  }, [token])

  if (status === "loading") {
    return <Loading message="Updating your group digest subscription" />
  } else if (status === "error" && error) {
    return <ResultMessage {...buildErrorResultMessageProps(error)} />
  } else if (status === "success" && data) {
    return <ResultMessage {...buildSuccessResultMessageProps(data)} />
  } else {
    return null
  }
}

type ResultMessageProps = {
  title: string
  message: string
  linkText?: string
  linkPath?: string
}

function ResultMessage({
  title,
  message,
  linkText,
  linkPath,
}: ResultMessageProps) {
  return (
    <div
      className="d-f fd-c ai-c ta-c"
      style={{ maxWidth: "32rem", margin: "auto" }}
    >
      <Heading level={2} text={title} />
      <p className="mt-2">{message}</p>
      {linkText && linkPath && (
        <Link to={linkPath} className="btn">
          {linkText}
        </Link>
      )}
    </div>
  )
}

function buildErrorResultMessageProps(error: APIError): ResultMessageProps {
  const detail = error.errors[0]?.detail ? error.errors[0].detail : null
  const title = "Your subscription was not updated"
  let message = ""
  let linkText, linkPath

  if (
    detail === "Token does not contain valid JSON" ||
    detail === "Token seems to be malformed"
  ) {
    message =
      "There seems to be something wrong with this link. We were unable to update your group digest subscription."
  } else if (detail === "Membership not found") {
    message =
      "You are no longer an active member of this group. If you think this is a mistake, please contact the group leader."
  } else if (detail === "Could not update subscription") {
    message = "We had trouble updating your subscription. Please try again."
    linkText = "Refresh"
    linkPath = window.location.pathname
  } else {
    message =
      "Something went wrong while updating your group digest subscription. Please try again."
    linkText = "Refresh"
    linkPath = window.location.pathname
  }

  return { title, message, linkText, linkPath }
}

function buildSuccessResultMessageProps(
  data: ResponseData,
): ResultMessageProps {
  const [group, person, revertToken] = data
  let title = ""
  let message = ""
  let linkText = ""
  const linkPath = `/groups/digest_subscriptions/${revertToken.attributes.value}`

  if (revertToken.type === "UnsubscribeToken") {
    title = "You are now subscribed"
    message = `You will receive daily digest emails at ${person.attributes.email_address} for ${group.attributes.name}. If this was a mistake, you can unsubscribe below.`
    linkText = "Unsubscribe"
  } else if (revertToken.type === "SubscribeToken") {
    title = "You are now unsubscribed"
    message = `You will no longer receive daily digest emails at ${person.attributes.email_address} for ${group.attributes.name}. If this was a mistake, you can resubscribe below.`
    linkText = "Resubscribe"
  }

  return { title, message, linkText, linkPath }
}
